import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import type { BottomSheetDefaultBackdropProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetBackdrop/types';
import { RsvpConfirmSheet } from '@/features/events/shared/RsvpConfirmSheet';
import {
  RsvpVisibilitySelector,
  rsvpVisibilityExplainer,
} from '@/features/events/shared/RsvpVisibilitySelector';
import { getCoverEventPosterImage } from '@/features/events/shared/FocalEventPosterImage';
import { FEED_POSTER_SNAPBACK_TIMING } from '@/features/events/shared/posterAnimation';
import { getPosterImageFrameStyle } from '@/features/events/shared/posterFocal';
import { resolveEventPosterURL } from '@/features/events/shared/posterURL';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { MetaBadge } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { getEventLimitNotice, getEventRSVPButtonLabel } from '@/features/events/eventTags';
import {
  isDetailEventFull,
  type RsvpSheetMode,
} from '@/features/events/eventDetail/eventDetailHelpers';
import { formatApproximateDistanceLabel, useUserOriginCoords } from '@/lib/location/locationOrigin';
import { type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';
import { formatEventDate, formatEventDayLabel, formatEventTime, formatEventTimeRange } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { cn } from '@/lib/utils';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { EVENT_POSTER_CONSTANTS, type RSVPVisibility } from '@/lib/api/apiTypes';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BadgeCheckIcon,
  BookmarkIcon,
  Building2Icon,
  CalendarDaysIcon,
  CalendarPlusIcon,
  CheckIcon,
  EyeIcon,
  FlagIcon,
  ImageOffIcon,
  LinkIcon,
  MapPinIcon,
  MoreHorizontalIcon,
  NavigationIcon,
  PencilLineIcon,
  ShareIcon,
  UserPlusIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  Animated,
  Image as RNImage,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import Reanimated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FullWindowOverlay } from 'react-native-screens';
import { SnapbackZoom } from 'react-native-zoom-toolkit';
import { Avatar as UiAvatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const DETAIL_SHEET_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});
const CONTAIN_TEXT_STYLE = Platform.select({
  web: { overflowWrap: 'anywhere', wordBreak: 'break-word' } as object,
});
const WEB_MOBILE_HERO_TITLE_CLIP_STYLE = Platform.select({
  web: { maxHeight: 34 * 3, overflow: 'hidden' } as object,
});

type EventDetailPosterImage = {
  public_url: string;
  focal_point_x?: number;
  focal_point_y?: number;
  focal_scale?: number;
};

type HeroPosterProps = {
  image: EventDetailPosterImage;
  index: number;
  contentWidth: number;
  heroHeight: number;
  isWeb: boolean;
  didSwipe: boolean;
  scrollRef: React.RefObject<ScrollView | null>;
  onOpenPoster: (index: number) => void;
  onZoomStart: () => void;
  onZoomEnd: () => void;
};

function HeroPoster({
  image,
  index,
  contentWidth,
  heroHeight,
  isWeb,
  didSwipe,
  scrollRef,
  onOpenPoster,
  onZoomStart,
  onZoomEnd,
}: HeroPosterProps) {
  const posterFrame = (
    <View style={{ width: contentWidth, height: heroHeight }}>
      <PosterImage image={image} />
    </View>
  );

  if (isWeb) {
    return <Pressable onPress={() => onOpenPoster(index)}>{posterFrame}</Pressable>;
  }

  return (
    <SnapbackZoom
      scrollRef={scrollRef as any}
      timingConfig={FEED_POSTER_SNAPBACK_TIMING}
      onTap={() => {
        if (!didSwipe) {
          onOpenPoster(index);
        }
      }}
      onPinchStart={onZoomStart}
      onGestureEnd={onZoomEnd}>
      {posterFrame}
    </SnapbackZoom>
  );
}

type RsvpBottomSheetProps = {
  rsvpSheetMode: RsvpSheetMode;
  event: RadiousEventDetail;
  snapPoints: string[];
  insets: ReturnType<typeof useSafeAreaInsets>;
  renderBackdrop: (props: BottomSheetDefaultBackdropProps) => React.ReactElement;
  visibility: RSVPVisibility;
  isRsvpMutationPending: boolean;
  onDismiss: () => void;
  onClose: () => void;
  onConfirm: () => void;
  onChangeVisibility: (val: RSVPVisibility) => void;
  onCancelRsvp: () => void;
};

export const RsvpBottomSheet = React.forwardRef<BottomSheetModal, RsvpBottomSheetProps>(
  function RsvpBottomSheet(
    {
      rsvpSheetMode,
      event,
      snapPoints,
      insets,
      renderBackdrop,
      visibility,
      isRsvpMutationPending,
      onDismiss,
      onClose,
      onConfirm,
      onChangeVisibility,
      onCancelRsvp,
    },
    ref
  ) {
    const userOrigin = useUserOriginCoords();

    return (
      <BottomSheetModal
        ref={ref}
        index={0}
        snapPoints={snapPoints}
        enablePanDownToClose
        backdropComponent={renderBackdrop}
        containerComponent={Platform.OS === 'ios' ? (FullWindowOverlay as any) : undefined}
        onDismiss={onDismiss}
        backgroundStyle={{
          backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
        handleIndicatorStyle={{
          backgroundColor: DESIGN_TOKENS.colors.borderStrong,
          width: 108,
          height: 5,
        }}
        style={{
          marginHorizontal: 0,
          shadowColor: '#000000',
          shadowOpacity: 0.28,
          shadowRadius: 18,
          shadowOffset: { width: 0, height: 8 },
        }}>
        <BottomSheetView
          style={{
            backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
            paddingHorizontal: 16,
            paddingBottom: insets.bottom + 12,
          }}>
          {rsvpSheetMode === 'manage' ? (
            <ManageRsvpSheetContent
              title={event.title}
              venueName={event.venue_name}
              startsAt={event.starts_at}
              visibility={visibility}
              isBusy={isRsvpMutationPending}
              onChangeVisibility={onChangeVisibility}
              onSaveVisibility={onConfirm}
              onCancelRsvp={onCancelRsvp}
              onClose={onClose}
            />
          ) : (
            <RsvpConfirmSheet
              title={event.title}
              posterURL={event.poster_url}
              posterImage={getCoverEventPosterImage(event)}
              dayLabel={formatEventDayLabel(event.starts_at)}
              distanceLabel={formatApproximateDistanceLabel(event.distance_miles, userOrigin)}
              timeLabel={formatEventTimeRange(event.starts_at, event.ends_at)}
              timeDetail={formatDetailSheetTime(event.starts_at)}
              venueName={event.venue_name}
              venueDetail={event.address || undefined}
              visibility={visibility}
              isBusy={isRsvpMutationPending}
              isFull={isDetailEventFull(event)}
              onChangeVisibility={onChangeVisibility}
              onConfirm={onConfirm}
              onClose={onClose}
            />
          )}
        </BottomSheetView>
      </BottomSheetModal>
    );
  }
);

export const EventDetailOverflowSheet = React.forwardRef<
  BottomSheetModal,
  {
    canManage: boolean;
    canCancel: boolean;
    hasVenuePage: boolean;
    hasDirections: boolean;
    showSkeletonPreview: boolean;
    insets: ReturnType<typeof useSafeAreaInsets>;
    renderBackdrop: (props: BottomSheetDefaultBackdropProps) => React.ReactElement;
    onAddToCalendar: () => void;
    onOpenVenue: () => void;
    onOpenDirections: () => void;
    onCopyLink: () => void;
    onEdit: () => void;
    onCancel: () => void;
    onReport: () => void;
    onPreviewSkeleton: () => void;
  }
>(function EventDetailOverflowSheet(
  {
    canManage,
    canCancel,
    hasVenuePage,
    hasDirections,
    showSkeletonPreview,
    insets,
    renderBackdrop,
    onAddToCalendar,
    onOpenVenue,
    onOpenDirections,
    onCopyLink,
    onEdit,
    onCancel,
    onReport,
    onPreviewSkeleton,
  },
  ref
) {
  const actionCount =
    2 +
    (canManage ? 1 : 0) +
    (canCancel ? 1 : 0) +
    (hasVenuePage ? 1 : 0) +
    (hasDirections ? 1 : 0) +
    (showSkeletonPreview ? 1 : 0);
  const overflowSnapPoints = React.useMemo(
    () => [`${Math.min(72, Math.max(34, 14 + actionCount * 9))}%`],
    [actionCount]
  );

  return (
    <BottomSheetModal
      ref={ref}
      index={0}
      snapPoints={overflowSnapPoints}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      containerComponent={Platform.OS === 'ios' ? (FullWindowOverlay as any) : undefined}
      backgroundStyle={{
        backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
      }}
      handleIndicatorStyle={{
        backgroundColor: DESIGN_TOKENS.colors.borderStrong,
        width: 96,
        height: 5,
      }}>
      <BottomSheetView
        className="gap-2 px-4 pt-1"
        style={{
          backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
          paddingBottom: insets.bottom + 12,
        }}>
        <HeroOverflowAction
          icon={CalendarPlusIcon}
          label="Add to calendar"
          onPress={onAddToCalendar}
        />
        {hasVenuePage ? (
          <HeroOverflowAction icon={MapPinIcon} label="Open venue" onPress={onOpenVenue} />
        ) : null}
        {hasDirections ? (
          <HeroOverflowAction icon={NavigationIcon} label="Directions" onPress={onOpenDirections} />
        ) : null}
        <HeroOverflowAction icon={LinkIcon} label="Copy link" onPress={onCopyLink} />
        {canManage ? (
          <HeroOverflowAction icon={PencilLineIcon} label="Edit event" onPress={onEdit} />
        ) : (
          <HeroOverflowAction
            icon={FlagIcon}
            label="Report issue"
            tone="danger"
            onPress={onReport}
          />
        )}
        {canCancel ? (
          <HeroOverflowAction
            icon={XCircleIcon}
            label="Cancel event"
            tone="danger"
            onPress={onCancel}
          />
        ) : null}
        {showSkeletonPreview ? (
          <HeroOverflowAction icon={EyeIcon} label="Preview skeleton" onPress={onPreviewSkeleton} />
        ) : null}
      </BottomSheetView>
    </BottomSheetModal>
  );
});

function HeroOverflowAction({
  icon,
  label,
  tone = 'default',
  onPress,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  tone?: 'default' | 'danger';
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      className="flex-row items-center gap-3 rounded-[18px] border border-border-strong bg-input/20 px-4 py-3"
      onPress={onPress}>
      <View className="size-9 items-center justify-center rounded-full bg-primary/15">
        <Icon as={icon} size={17} className={tone === 'danger' ? 'text-primary' : 'text-primary'} />
      </View>
      <Text
        className={cn(
          'flex-1 text-[15px] font-semibold',
          tone === 'danger' ? 'text-primary' : 'text-foreground'
        )}>
        {label}
      </Text>
    </Pressable>
  );
}

export const HeroSection = React.memo(function HeroSection({
  event,
  posterImages,
  contentWidth,
  scrollRef,
  onBack,
  onOpenPoster,
  onShare,
  onOpenMoreOptions,
}: {
  event: RadiousEventDetail;
  posterImages: EventDetailPosterImage[];
  contentWidth: number;
  scrollRef: React.RefObject<ScrollView | null>;
  onBack: () => void;
  onOpenPoster: (index: number) => void;
  onShare: () => void;
  onOpenMoreOptions: () => void;
}) {
  const { height: screenHeight } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';
  const heroHeight = isWeb
    ? Math.max(260, Math.min(480, Math.round(contentWidth * 0.56)))
    : Math.round(screenHeight * 0.42);
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const [isZooming, setIsZooming] = React.useState(false);
  const [didSwipe, setDidSwipe] = React.useState(false);
  const heroImages = React.useMemo(() => posterImages.slice(0, 5), [posterImages]);
  const hasMultipleImages = heroImages.length > 1;
  const overlayAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: withTiming(isZooming ? 0 : 1, { duration: 150 }),
    }),
    [isZooming]
  );

  React.useEffect(() => {
    setActiveImageIndex(0);
    setIsZooming(false);
    setDidSwipe(false);
  }, [event.id]);

  React.useEffect(() => {
    setActiveImageIndex((currentIndex) =>
      Math.min(currentIndex, Math.max(0, heroImages.length - 1))
    );
  }, [heroImages.length]);

  const handleZoomStart = React.useCallback(() => setIsZooming(true), []);
  const handleZoomEnd = React.useCallback(() => setIsZooming(false), []);

  return (
    <View
      className={`relative ${isWeb ? 'overflow-hidden rounded-[30px]' : ''}`}
      style={
        isWeb
          ? {
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surface,
            }
          : undefined
      }>
      <View style={{ height: heroHeight }} className="w-full overflow-hidden">
        {heroImages.length > 0 ? (
          hasMultipleImages && contentWidth > 0 ? (
            <ScrollView
              horizontal
              pagingEnabled
              bounces={false}
              contentContainerStyle={{ height: heroHeight }}
              decelerationRate="fast"
              snapToInterval={contentWidth}
              showsHorizontalScrollIndicator={false}
              scrollEventThrottle={16}
              style={{ width: contentWidth, height: heroHeight }}
              onScrollBeginDrag={() => setDidSwipe(true)}
              onMomentumScrollEnd={(scrollEvent) => {
                const index = Math.round(scrollEvent.nativeEvent.contentOffset.x / contentWidth);
                setActiveImageIndex(Math.max(0, Math.min(index, heroImages.length - 1)));
                setTimeout(() => setDidSwipe(false), 100);
              }}>
              {heroImages.map((image, index) => (
                <View
                  key={image.public_url}
                  style={{ width: contentWidth, height: heroHeight, overflow: 'hidden' }}>
                  <HeroPoster
                    image={image}
                    index={index}
                    contentWidth={contentWidth}
                    heroHeight={heroHeight}
                    isWeb={isWeb}
                    didSwipe={didSwipe}
                    scrollRef={scrollRef}
                    onOpenPoster={onOpenPoster}
                    onZoomStart={handleZoomStart}
                    onZoomEnd={handleZoomEnd}
                  />
                </View>
              ))}
            </ScrollView>
          ) : (
            <HeroPoster
              image={heroImages[0]}
              index={0}
              contentWidth={contentWidth}
              heroHeight={heroHeight}
              isWeb={isWeb}
              didSwipe={didSwipe}
              scrollRef={scrollRef}
              onOpenPoster={onOpenPoster}
              onZoomStart={handleZoomStart}
              onZoomEnd={handleZoomEnd}
            />
          )
        ) : (
          <View
            className="h-full w-full items-center justify-center"
            style={{ backgroundColor: DESIGN_TOKENS.colors.canvasInset }}>
            <Icon as={ImageOffIcon} size={40} className="text-white/35" />
          </View>
        )}

        <Reanimated.View
          pointerEvents="none"
          style={[
            { position: 'absolute', left: 0, right: 0, top: 0, height: '18%' },
            overlayAnimatedStyle,
          ]}>
          <LinearGradient
            colors={['rgba(0,0,0,0.45)', 'transparent']}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
          />
        </Reanimated.View>

        <Reanimated.View
          pointerEvents="none"
          style={[
            { position: 'absolute', left: 0, right: 0, bottom: 0, height: '55%' },
            overlayAnimatedStyle,
          ]}>
          <LinearGradient
            colors={[
              'transparent',
              'rgba(0,0,0,0.55)',
              'rgba(0,0,0,0.82)',
              DESIGN_TOKENS.colors.canvas,
            ]}
            locations={[0, 0.4, 0.75, 1]}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
          />
        </Reanimated.View>

        {hasMultipleImages ? (
          <Reanimated.View
            pointerEvents="none"
            style={[
              {
                position: 'absolute',
                bottom: 12,
                left: 0,
                right: 0,
                zIndex: 20,
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 5,
              },
              overlayAnimatedStyle,
            ]}>
            {heroImages.map((image, index) => (
              <View
                key={`${image.public_url}-dot`}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor:
                    index === activeImageIndex
                      ? 'rgba(255,255,255,0.95)'
                      : 'rgba(255,255,255,0.35)',
                }}
              />
            ))}
          </Reanimated.View>
        ) : null}

        <Reanimated.View
          pointerEvents="none"
          style={[{ position: 'absolute', left: 0, right: 0, bottom: 0 }, overlayAnimatedStyle]}>
          <View
            className={cn('absolute inset-x-0 bottom-0 px-4', hasMultipleImages ? 'pb-8' : 'pb-3')}>
            <Text
              variant="h2"
              className="text-[28px] font-extrabold tracking-tight text-white"
              numberOfLines={Platform.OS === 'web' ? undefined : 3}
              ellipsizeMode="tail"
              style={[CONTAIN_TEXT_STYLE, WEB_MOBILE_HERO_TITLE_CLIP_STYLE]}>
              {event.title}
            </Text>
          </View>
        </Reanimated.View>
      </View>

      <Reanimated.View
        pointerEvents={isZooming ? 'none' : 'auto'}
        style={[
          {
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 16,
            paddingTop: 12,
          },
          overlayAnimatedStyle,
        ]}>
        <HeroIconButton icon={ArrowLeftIcon} label="Back" onPress={onBack} />
        <View className="flex-row gap-3">
          <HeroIconButton icon={ShareIcon} label="Share event" onPress={onShare} />
          <HeroIconButton
            icon={MoreHorizontalIcon}
            label="More options"
            onPress={onOpenMoreOptions}
          />
        </View>
      </Reanimated.View>
    </View>
  );
});

export function HeroAttendeeSummary({
  attendees,
  totalCount,
  canOpen,
  onPress,
  avatarSize = 20,
  textColor = 'rgba(255,255,255,0.55)',
}: {
  attendees: RadiousEventDetail['attendee_preview'];
  totalCount: number;
  canOpen: boolean;
  onPress: () => void;
  avatarSize?: number;
  textColor?: string;
}) {
  const visibleAttendees = attendees.slice(0, 3);
  const avatarRadius = avatarSize / 2;
  const avatarOverlap = Math.max(6, Math.round(avatarSize * 0.32));

  return (
    <Pressable className="flex-row items-center gap-2" disabled={!canOpen} onPress={onPress}>
      <View className="flex-row items-center">
        {visibleAttendees.map((attendee, index) => (
          <UiAvatar
            key={attendee.user_id}
            className="items-center justify-center border-[1.5px] border-background bg-canvas-inset"
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: avatarRadius,
              marginLeft: index === 0 ? 0 : -avatarOverlap,
              zIndex: 3 - index,
            }}>
            {attendee.avatar_url.trim().length > 0 ? (
              <AvatarImage
                source={{ uri: attendee.avatar_url }}
                style={{ width: avatarSize, height: avatarSize, borderRadius: avatarRadius }}
              />
            ) : null}
            <AvatarFallback className="bg-transparent">
              <Text className="text-[9px] font-semibold text-foreground">
                {getProfileInitials(attendee.display_name || attendee.username)}
              </Text>
            </AvatarFallback>
          </UiAvatar>
        ))}
      </View>
      <Text variant="small" className="text-[13px] font-medium" style={{ color: textColor }}>
        {totalCount} going
      </Text>
    </Pressable>
  );
}

function HeroIconButton({
  icon,
  label,
  onPress,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      className="size-11 items-center justify-center rounded-full border border-border-strong bg-black/60"
      onPress={onPress}>
      <Icon as={icon} size={17} className="text-white" />
    </Pressable>
  );
}

export function EventEndedBanner({ event }: { event: RadiousEventDetail }) {
  const endedAt = event.ends_at || event.starts_at;

  return (
    <View className="rounded-[20px] border border-border-strong bg-surface-raised px-4 py-3">
      <Text className="text-[14px] font-semibold">This event has ended</Text>
      <Text variant="small" className="mt-1 text-muted-foreground">
        Ended {formatEventDate(endedAt)} at {formatEventTime(endedAt)}. Details stay here for
        reference.
      </Text>
    </View>
  );
}

export function CancelledEventBanner({
  event,
  onOpenVenueProfile,
  onBrowseFeed,
}: {
  event: RadiousEventDetail;
  onOpenVenueProfile?: () => void;
  onBrowseFeed: () => void;
}) {
  return (
    <View className="-mt-2 rounded-[24px] border border-border-strong bg-surface p-4">
      <View className="flex-row items-center gap-2">
        <Icon as={XCircleIcon} size={18} className="text-primary" />
        <Text className="text-[16px] font-semibold text-foreground">Event canceled</Text>
      </View>
      <Text variant="small" className="mt-2 text-muted-foreground">
        This page stays up as a record of the event.
      </Text>
      {event.cancellation_message ? (
        <View className="mt-4 border-l-2 border-primary pl-3">
          <Text variant="small" className="uppercase tracking-[1.4px] text-muted-foreground">
            Host note
          </Text>
          <Text variant="small" className="mt-1 leading-6 text-foreground">
            {event.cancellation_message}
          </Text>
        </View>
      ) : null}

      <View className="mt-5 gap-3">
        {onOpenVenueProfile ? (
          <Pressable
            className="h-12 items-center justify-center rounded-[18px] bg-accent"
            onPress={onOpenVenueProfile}>
            <Text className="font-semibold text-accent-foreground" numberOfLines={1}>
              View venue page
            </Text>
          </Pressable>
        ) : null}
        <Pressable
          className="h-12 items-center justify-center rounded-[18px] border border-border bg-input/30"
          onPress={onBrowseFeed}>
          <Text className="font-semibold text-foreground" numberOfLines={1}>
            Browse events tonight
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

export const ActionRow = React.memo(function ActionRow({
  event,
  isBusy,
  isCalendarBusy,
  onOpenRSVP,
  onManageRSVP,
  onAddToCalendar,
  onToggleSave,
}: {
  event: RadiousEventDetail;
  isBusy: boolean;
  isCalendarBusy: boolean;
  onOpenRSVP: () => void;
  onManageRSVP: () => void;
  onAddToCalendar: () => void;
  onToggleSave: () => void;
}) {
  const isGoing = event.viewer_rsvp_status === 'going';
  const isSaved = event.is_saved;
  const isFull = isDetailEventFull(event);
  const rsvpLabel = getEventRSVPButtonLabel(event);
  const limitNotice = getEventLimitNotice(event);

  return (
    <View className="-mt-2">
      <View className="flex-row gap-3">
        <Button
          variant={isSaved ? 'default' : 'outline'}
          className="items-center justify-center"
          style={{
            flex: 1,
            minHeight: 52,
            backgroundColor: isSaved
              ? DESIGN_TOKENS.colors.accent
              : DESIGN_TOKENS.colors.surfaceRaised,
            borderWidth: isSaved ? 0 : 1,
            borderColor: isSaved ? 'transparent' : DESIGN_TOKENS.colors.borderStrong,
          }}
          disabled={isBusy}
          onPress={onToggleSave}>
          <Icon
            as={BookmarkIcon}
            size={16}
            className={isSaved ? 'text-primary-foreground' : 'text-muted-foreground'}
            {...({ fill: isSaved ? 'currentColor' : 'transparent' } as any)}
          />
          <Text
            className={cn('text-[14px]', isSaved ? 'text-primary-foreground' : 'text-foreground')}>
            {isSaved ? 'Saved' : 'Save'}
          </Text>
        </Button>

        <Button
          className="items-center justify-center"
          style={{
            flex: 2,
            minHeight: 52,
            backgroundColor: isGoing
              ? DESIGN_TOKENS.colors.surfaceAccent
              : isFull
                ? DESIGN_TOKENS.colors.surfaceRaised
                : DESIGN_TOKENS.colors.accent,
            borderWidth: isGoing || isFull ? 1 : 0,
            borderColor: isGoing
              ? DESIGN_TOKENS.colors.accentEdge
              : isFull
                ? DESIGN_TOKENS.colors.borderStrong
                : 'transparent',
            shadowColor: isGoing ? DESIGN_TOKENS.colors.accent : '#000000',
            shadowOpacity: isGoing ? 0.14 : isFull ? 0.08 : 0.28,
            shadowRadius: isGoing ? 16 : 10,
            shadowOffset: { width: 0, height: 6 },
            elevation: isGoing ? 8 : 0,
          }}
          disabled={isBusy || isFull}
          onPress={isGoing ? onManageRSVP : onOpenRSVP}>
          <Icon
            as={isGoing ? CheckIcon : CalendarDaysIcon}
            size={17}
            style={{
              color: isGoing
                ? DESIGN_TOKENS.colors.accent
                : isFull
                  ? undefined
                  : DESIGN_TOKENS.colors.canvasAlt,
            }}
            className={cn(isFull && 'text-muted-foreground')}
          />
          <Text
            className={cn('text-[15px] font-semibold', isFull && 'text-muted-foreground')}
            style={{
              color: isGoing
                ? DESIGN_TOKENS.colors.accent
                : isFull
                  ? undefined
                  : DESIGN_TOKENS.colors.canvasAlt,
            }}>
            {rsvpLabel}
          </Text>
        </Button>
      </View>

      {Platform.OS !== 'web' ? (
        <Pressable
          className="mt-3 flex-row items-center justify-center gap-2 self-center py-1"
          disabled={isCalendarBusy}
          onPress={onAddToCalendar}>
          <Icon as={CalendarPlusIcon} size={14} className="text-muted-foreground" />
          <Text variant="small" className="text-muted-foreground">
            Add to Calendar
          </Text>
        </Pressable>
      ) : null}

      {limitNotice ? (
        <View className="mt-3 items-center">
          <MetaBadge label={limitNotice} tone="warm" />
        </View>
      ) : null}
    </View>
  );
});

export const OwnerStatsCard = React.memo(function OwnerStatsCard({
  event,
  isCancelling,
  onCancel,
  onEdit,
}: {
  event: RadiousEventDetail;
  isCancelling: boolean;
  onCancel: () => void;
  onEdit: () => void;
}) {
  const stats = event.owner_stats;

  return (
    <View className="-mt-2 rounded-[24px] border border-border-strong bg-surface p-4">
      <View className="gap-3">
        <View className="flex-row gap-3">
          <OwnerStatItem icon={EyeIcon} value={stats?.views ?? 0} label="views" />
          <OwnerStatItem icon={CheckIcon} value={stats?.going ?? event.rsvp_count} label="going" />
        </View>
        <View className="flex-row gap-3">
          <OwnerStatItem icon={ShareIcon} value={stats?.shares ?? 0} label="shares" />
          <OwnerStatItem
            icon={MapPinIcon}
            value={`${stats?.nearby_percent ?? 0}%`}
            label="nearby"
          />
        </View>
      </View>

      <View className="mt-4 flex-row gap-3">
        <Button
          variant="outline"
          className="flex-1 items-center justify-center"
          style={{
            backgroundColor: DESIGN_TOKENS.colors.canvasInset,
            borderColor: DESIGN_TOKENS.colors.accentPressed,
          }}
          disabled={isCancelling}
          onPress={onCancel}>
          <Icon as={XCircleIcon} size={16} style={{ color: DESIGN_TOKENS.colors.textSoft }} />
          <Text style={{ color: DESIGN_TOKENS.colors.textSoft }}>
            {isCancelling ? 'Canceling...' : 'Cancel Event'}
          </Text>
        </Button>
        <Button
          className="flex-1 items-center justify-center"
          disabled={isCancelling}
          onPress={onEdit}>
          <Icon as={PencilLineIcon} size={16} style={{ color: DESIGN_TOKENS.colors.canvasAlt }} />
          <Text style={{ color: DESIGN_TOKENS.colors.canvasAlt }}>Edit Event</Text>
        </Button>
      </View>
    </View>
  );
});

export function CancelEventModal({
  visible,
  message,
  isSubmitting,
  onChangeMessage,
  onClose,
  onConfirm,
}: {
  visible: boolean;
  message: string;
  isSubmitting: boolean;
  onChangeMessage: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View className="flex-1 bg-black/70">
        <Pressable className="absolute inset-0" onPress={onClose} />
        <KeyboardAvoidingView
          className="flex-1"
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              justifyContent: 'center',
              paddingHorizontal: 20,
            }}
            keyboardShouldPersistTaps="handled">
            <View className="w-full max-w-[420px] self-center rounded-[28px] border border-border-strong bg-surface p-5">
              <View className="flex-row items-start justify-between gap-4">
                <View className="flex-1">
                  <Text className="text-[20px] font-semibold text-foreground">
                    Cancel this event?
                  </Text>
                  <Text variant="small" className="mt-2 text-muted-foreground">
                    This removes it from feeds, keeps the page live by direct link, and notifies
                    everyone marked going.
                  </Text>
                </View>
                <Pressable
                  className="size-10 items-center justify-center rounded-full border border-border-strong bg-surface-raised"
                  disabled={isSubmitting}
                  onPress={onClose}>
                  <Icon as={XIcon} size={16} className="text-muted-foreground" />
                </Pressable>
              </View>

              <View className="mt-5 gap-2">
                <Text
                  variant="small"
                  className="font-semibold uppercase tracking-[1.6px] text-muted-foreground">
                  Optional Message To Attendees
                </Text>
                <TextInput
                  value={message}
                  onChangeText={onChangeMessage}
                  placeholder="Share a quick reason or update"
                  placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
                  editable={!isSubmitting}
                  multiline
                  scrollEnabled
                  className="min-h-[112px] rounded-[20px] border border-border-strong bg-surface-raised px-4 py-[14px] text-[15px] text-foreground"
                  textAlignVertical="top"
                  style={{ minHeight: 112 }}
                />
              </View>

              <View className="mt-5 flex-row gap-3">
                <Button
                  className="flex-1 items-center justify-center"
                  style={{ backgroundColor: DESIGN_TOKENS.colors.accentPressed }}
                  disabled={isSubmitting}
                  onPress={onConfirm}>
                  <Text className="text-primary-foreground">
                    {isSubmitting ? 'Canceling...' : 'Cancel Event'}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 items-center justify-center"
                  disabled={isSubmitting}
                  onPress={onClose}>
                  <Text>Keep Event</Text>
                </Button>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

function OwnerStatItem({
  icon,
  value,
  label,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  value: number | string;
  label: string;
}) {
  return (
    <View
      className="flex-1 flex-row items-center gap-2 rounded-[18px] border p-3"
      style={{
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
      }}>
      <Icon as={icon} size={16} color={DESIGN_TOKENS.colors.accent} />
      <Text variant="small" className="flex-1 font-semibold text-foreground">
        {value} {label}
      </Text>
    </View>
  );
}

export function ManageRsvpSheetContent({
  title,
  venueName,
  startsAt,
  visibility,
  isBusy,
  onChangeVisibility,
  onSaveVisibility,
  onCancelRsvp,
  onClose,
}: {
  title: string;
  venueName: string;
  startsAt: string;
  visibility: RSVPVisibility;
  isBusy: boolean;
  onChangeVisibility: (visibility: RSVPVisibility) => void;
  onSaveVisibility: () => void;
  onCancelRsvp: () => void;
  onClose: () => void;
}) {
  return (
    <View className="pb-3 pt-1">
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1 gap-1">
          <View className="flex-row items-center gap-2">
            <View className="size-8 items-center justify-center rounded-full bg-primary/15">
              <Icon as={CheckIcon} size={15} className="text-primary" />
            </View>
            <Text variant="large" className="text-xl font-semibold text-foreground">
              You&apos;re going
            </Text>
          </View>
          <Text variant="small" className="text-muted-foreground" numberOfLines={2}>
            {title}
          </Text>
        </View>
        <Pressable
          className="size-10 items-center justify-center rounded-full border border-border bg-input/30"
          onPress={onClose}>
          <Icon as={XIcon} size={16} className="text-muted-foreground" />
        </Pressable>
      </View>

      <View className="mt-4 gap-2">
        <View className="rounded-[16px] border border-border bg-input/20 p-3">
          <Text className="font-medium text-foreground" numberOfLines={1}>
            {venueName}
          </Text>
          <Text variant="small" className="mt-0.5 text-muted-foreground">
            {formatDetailSheetTime(startsAt)}
          </Text>
        </View>
        <View className="gap-2">
          <RsvpVisibilitySelector
            visibility={visibility}
            disabled={isBusy}
            onChangeVisibility={onChangeVisibility}
          />
          <Text className="text-center text-[11px] leading-4 text-white/35">
            {rsvpVisibilityExplainer(visibility)}
          </Text>
          <Pressable
            className="h-12 flex-row items-center justify-center gap-2 rounded-[18px] bg-primary"
            disabled={isBusy}
            onPress={onSaveVisibility}>
            <Icon as={CheckIcon} size={15} className="text-primary-foreground" />
            <Text className="font-semibold text-primary-foreground">
              {isBusy ? 'Saving...' : 'Save visibility'}
            </Text>
          </Pressable>
        </View>
        <Pressable
          className="flex-row items-center gap-3 rounded-[16px] border border-primary/30 bg-primary/10 p-3"
          disabled={isBusy}
          onPress={onCancelRsvp}>
          <View className="size-8 items-center justify-center rounded-full bg-primary/15">
            <Icon as={XCircleIcon} size={17} className="text-primary" />
          </View>
          <View className="flex-1 gap-0.5">
            <Text className="text-[14px] font-semibold text-primary">Not going anymore</Text>
            <Text className="text-[12px] text-muted-foreground">Remove this from your plans.</Text>
          </View>
        </Pressable>

        <Pressable className="flex-row items-center gap-3 rounded-[16px] p-3" onPress={onClose}>
          <Text className="flex-1 text-center font-semibold text-muted-foreground">Keep going</Text>
        </Pressable>
      </View>
    </View>
  );
}

export function formatDetailSheetTime(startsAt: string) {
  return `${formatEventDate(startsAt)} · ${DETAIL_SHEET_TIME_FORMATTER.format(new Date(startsAt))}`;
}

export function DetailRow({
  icon,
  title,
  value,
  subtitle,
  subtitleClassName,
  subtitleStyle,
  actionIcon,
  onAction,
  onPress,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  title?: string;
  value: string;
  subtitle?: string;
  subtitleClassName?: string;
  subtitleStyle?: object;
  actionIcon?: React.ComponentProps<typeof Icon>['as'];
  onAction?: () => void;
  onPress?: () => void;
}) {
  const leadingContent = (
    <>
      <View className="mt-1 w-5 items-center">
        <Icon as={icon} size={16} className="text-muted-foreground" />
      </View>
      <View className="flex-1 gap-1">
        {title ? (
          <Text className="text-[18px] font-semibold leading-6 text-foreground">{title}</Text>
        ) : null}
        <Text variant="default" className="leading-6 text-foreground">
          {value}
        </Text>
        {subtitle ? (
          <Text
            variant="small"
            className={cn('text-muted-foreground', subtitleClassName)}
            style={subtitleStyle}>
            {subtitle}
          </Text>
        ) : null}
      </View>
    </>
  );

  return (
    <View className="flex-row items-start gap-3">
      {onPress ? (
        <Pressable className="flex-1 flex-row items-start gap-3" onPress={onPress}>
          {leadingContent}
        </Pressable>
      ) : (
        <View className="flex-1 flex-row items-start gap-3">{leadingContent}</View>
      )}
      {actionIcon && onAction ? (
        <Pressable
          className="size-10 items-center justify-center rounded-full border border-border-strong bg-surface-raised"
          onPress={onAction}>
          <Icon as={actionIcon} size={16} className="text-muted-foreground" />
        </Pressable>
      ) : null}
    </View>
  );
}

export function VenueSummaryCard({
  venueName,
  venueCategoryLabel,
  venueLogoURL,
  venueEventCount,
  isVenueVerified,
  categoryIcon,
  categoryColor,
  categoryBackgroundColor,
  onPress,
  onVenueAction,
  venueActionHint,
  venueActionLabel,
  isFollowing,
  isFollowBusy,
  canFollow,
  onFollow,
}: {
  venueName: string;
  venueCategoryLabel?: string | null;
  venueLogoURL?: string;
  venueEventCount?: number | null;
  isVenueVerified: boolean;
  categoryIcon: React.ComponentProps<typeof Icon>['as'];
  categoryColor: string;
  categoryBackgroundColor: string;
  onPress?: () => void;
  onVenueAction?: () => void;
  venueActionHint?: string;
  venueActionLabel?: string;
  isFollowing?: boolean;
  isFollowBusy?: boolean;
  canFollow?: boolean;
  onFollow?: () => void;
}) {
  const hasVenueLink = Boolean(onPress);
  const supportingCopy = hasVenueLink
    ? venueEventCount && venueEventCount > 1
      ? `${venueEventCount} events here on Radious`
      : 'Open the venue page'
    : null;
  const headerContent = (
    <>
      <Text variant="small" className="font-semibold text-muted-foreground">
        Venue
      </Text>

      <View className="mt-3 flex-row items-start gap-3">
        <View
          className="size-[52px] items-center justify-center overflow-hidden rounded-full border"
          style={{
            borderColor: hasVenueLink ? categoryColor : DESIGN_TOKENS.colors.borderStrong,
            backgroundColor: hasVenueLink
              ? categoryBackgroundColor
              : DESIGN_TOKENS.colors.surfaceRaised,
          }}>
          {venueLogoURL ? (
            <Image
              source={{ uri: venueLogoURL }}
              style={{ width: 52, height: 52 }}
              contentFit="cover"
              cachePolicy="memory-disk"
              transition={200}
            />
          ) : (
            <Icon
              as={hasVenueLink ? categoryIcon : Building2Icon}
              size={24}
              color={hasVenueLink ? categoryColor : DESIGN_TOKENS.colors.textMuted}
            />
          )}
        </View>

        <View className="flex-1 gap-1">
          <View className="flex-row flex-wrap items-center gap-x-1.5 gap-y-1 pr-3">
            <Text
              className="text-[18px] font-semibold text-foreground"
              numberOfLines={2}
              style={CONTAIN_TEXT_STYLE}>
              {venueName}
            </Text>
            {isVenueVerified ? (
              <Icon as={BadgeCheckIcon} size={18} className="text-primary" />
            ) : null}
          </View>

          {venueCategoryLabel ? (
            <Text variant="small" className="text-muted-foreground">
              {venueCategoryLabel}
            </Text>
          ) : null}

          {supportingCopy ? (
            <Text
              variant="small"
              className="leading-5 text-muted-foreground"
              numberOfLines={2}
              style={CONTAIN_TEXT_STYLE}>
              {supportingCopy}
            </Text>
          ) : null}
        </View>

        {canFollow && onFollow ? (
          <Button
            variant={isFollowing ? 'outline' : 'default'}
            size="sm"
            className="mt-0.5 items-center justify-center"
            disabled={isFollowBusy}
            onPress={onFollow}>
            <Icon
              as={UserPlusIcon}
              size={15}
              className={isFollowing ? 'text-muted-foreground' : 'text-primary-foreground'}
            />
            <Text>{isFollowing ? 'Following' : 'Follow'}</Text>
          </Button>
        ) : hasVenueLink ? (
          <View
            className="mt-0.5 size-10 items-center justify-center rounded-full border"
            style={{
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
            }}>
            <Icon as={ArrowRightIcon} size={16} className="text-muted-foreground" />
          </View>
        ) : null}
      </View>
    </>
  );

  return (
    <View className="mt-3 rounded-[24px] border border-border-strong bg-surface">
      {onPress ? (
        <Pressable className="p-4" onPress={onPress}>
          {headerContent}
        </Pressable>
      ) : (
        <View className="p-4">{headerContent}</View>
      )}

      {onVenueAction && venueActionLabel ? (
        <>
          <View className="h-px bg-border" />
          <Pressable
            className="flex-row items-center justify-between px-4 py-3"
            onPress={onVenueAction}>
            <Text variant="small" className="text-muted-foreground">
              {venueActionHint ?? 'Business tools live in Backstage'}
            </Text>
            <Text variant="small" className="font-semibold text-foreground">
              {venueActionLabel}
            </Text>
          </Pressable>
        </>
      ) : null}
    </View>
  );
}

function PosterImage({ image }: { image: EventDetailPosterImage }) {
  const uri = resolveEventPosterURL(image.public_url);
  const focalPointX = image.focal_point_x ?? EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x;
  const focalPointY = image.focal_point_y ?? EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y;
  const focalScale = image.focal_scale ?? 1;
  const [imgError, setImgError] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [posterSize, setPosterSize] = React.useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
  const imageOpacity = React.useRef(new Animated.Value(0)).current;
  const imageFrameStyle = getPosterImageFrameStyle(
    imageSize.width || null,
    imageSize.height || null,
    posterSize.width,
    posterSize.height,
    focalPointX,
    focalPointY,
    focalScale
  );

  React.useEffect(() => {
    setImgError(false);
    setIsLoaded(false);
    setImageSize({ width: 0, height: 0 });
    imageOpacity.setValue(0);
  }, [imageOpacity, uri]);

  React.useEffect(() => {
    if (!uri) {
      return;
    }

    RNImage.getSize(
      uri,
      (width, height) => setImageSize({ width, height }),
      () => undefined
    );
  }, [uri]);

  React.useEffect(() => {
    if (!isLoaded || imgError) {
      return;
    }

    Animated.timing(imageOpacity, {
      toValue: 1,
      duration: 180,
      useNativeDriver: true,
    }).start();
  }, [imageOpacity, imgError, isLoaded]);

  if (imgError) {
    return (
      <View
        className="h-full w-full items-center justify-center"
        style={{ backgroundColor: DESIGN_TOKENS.colors.canvasInset }}>
        <Icon as={ImageOffIcon} size={40} className="text-white/35" />
      </View>
    );
  }

  return (
    <View
      className="h-full w-full"
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        if (width !== posterSize.width || height !== posterSize.height) {
          setPosterSize({ width, height });
        }
      }}
      style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}>
      <Animated.View style={{ flex: 1, opacity: imageOpacity }}>
        <Animated.Image
          source={{ uri }}
          resizeMode={imageFrameStyle ? 'stretch' : 'cover'}
          className={imageFrameStyle ? undefined : 'h-full w-full'}
          style={imageFrameStyle ? [{ position: 'absolute' }, imageFrameStyle] : undefined}
          onLoad={(event) => {
            const source = event.nativeEvent.source;
            if (source?.width && source?.height) {
              setImageSize({ width: source.width, height: source.height });
            }
            setIsLoaded(true);
          }}
          onError={() => setImgError(true)}
        />
      </Animated.View>
    </View>
  );
}
